import api from '@/services/api';
import { vehiclesPath } from '.';
import type {
    ChangeRegistrationInput,
    VehicleAmendment,
    VehicleAmendmentsPage,
    VehicleMileage,
    VehicleRegistration,
} from '../types/vehicleRegistration.types';

export const VEHICLE_AMENDMENTS_PER_PAGE = 20;

type RawRecord = Record<string, unknown>;

// Page numbers are 1-based for the shared `Pagination`; only `total` may be 0.
const readCount = (value: unknown, min: number, fallback: number): number => {
    const parsed = Number(value);

    return Number.isInteger(parsed) && parsed >= min ? parsed : fallback;
};

const readString = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);

const readStringMap = (value: unknown): Record<string, string | null> => {
    if (typeof value !== 'object' || value === null) return {};

    return Object.fromEntries(
        Object.entries(value as RawRecord).map(([key, entry]) => [
            key,
            typeof entry === 'string' ? entry : typeof entry === 'number' && Number.isFinite(entry) ? String(entry) : null,
        ]),
    );
};

/** Reads only the plate and its timestamp off the status row, so a mileage or SORN flag
 *  cannot reach the agreement panel. A `data: null` body maps to a null plate. */
const mapVehicleRegistration = (vehicleId: string, raw: unknown): VehicleRegistration => {
    const row = (typeof raw === 'object' && raw !== null ? raw : {}) as RawRecord;

    return {
        vehicleId,
        vrm: readString(row.vrm),
        recordedAt: readString(row.created_at),
    };
};

const mapVehicleMileage = (vehicleId: string, raw: unknown): VehicleMileage => {
    const row = (typeof raw === 'object' && raw !== null ? raw : {}) as RawRecord;
    const mileage = row.mileage;

    return {
        vehicleId,
        mileage: typeof mileage === 'number' && Number.isInteger(mileage) && mileage >= 0 ? mileage : null,
        recordedOn: readString(row.mileage_recorded_date),
    };
};

export const mapVehicleAmendment = (raw: unknown): VehicleAmendment => {
    const row = (typeof raw === 'object' && raw !== null ? raw : {}) as RawRecord;

    return {
        id: readString(row.id) ?? '',
        amendmentType: readString(row.amendment_type) ?? '',
        reason: readString(row.reason),
        agreementId: readString(row.agreement_id) ?? '',
        vehicleStatusId: readString(row.vehicle_status_id),
        changedFields: Array.isArray(row.changed_fields)
            ? row.changed_fields.filter((field): field is string => typeof field === 'string')
            : [],
        oldValues: readStringMap(row.old_values),
        newValues: readStringMap(row.new_values),
        recordedAt: readString(row.recorded_at) ?? '',
        actorType: readString(row.actor_type),
        actorId: readString(row.actor_id),
        actorName: readString(row.actor_name),
    };
};

export const fetchVehicleRegistration = async (vehicleId: string): Promise<VehicleRegistration> => {
    const response = await api.get(`${vehiclesPath}/${vehicleId}/status`);

    return mapVehicleRegistration(vehicleId, response.data?.data);
};

export const fetchVehicleMileage = async (vehicleId: string): Promise<VehicleMileage> => {
    const response = await api.get(`${vehiclesPath}/${vehicleId}/status`);

    return mapVehicleMileage(vehicleId, response.data?.data);
};

export const changeVehicleRegistration = async (
    vehicleId: string,
    input: ChangeRegistrationInput,
): Promise<VehicleAmendment> => {
    const response = await api.post(`${vehiclesPath}/${vehicleId}/registration-changes`, {
        new_vrm: input.newVrm,
        reason: input.reason,
        same_vehicle_confirmed: input.sameVehicleConfirmed,
        agreement_id: input.agreementId,
    });

    return mapVehicleAmendment(response.data?.data);
};

export const fetchVehicleAmendments = async (
    vehicleId: string,
    params: { page: number; perPage: number },
): Promise<VehicleAmendmentsPage> => {
    const response = await api.get(`${vehiclesPath}/${vehicleId}/amendments`, {
        params: { page: params.page, per_page: params.perPage },
    });
    const body = (typeof response.data === 'object' && response.data !== null ? response.data : {}) as RawRecord;
    const meta = (typeof body.meta === 'object' && body.meta !== null ? body.meta : {}) as RawRecord;

    return {
        amendments: (Array.isArray(body.data) ? body.data : []).map(mapVehicleAmendment),
        meta: {
            currentPage: readCount(meta.current_page, 1, params.page),
            lastPage: readCount(meta.last_page, 1, 1),
            perPage: readCount(meta.per_page, 1, params.perPage),
            total: readCount(meta.total, 0, 0),
        },
    };
};
